import type {CSSProperties} from 'react';

/**
 * Every avatar size, defined once. `avatarSizeStyle` exposes a row as CSS variables that `Avatar`,
 * `AvatarSkeleton`, `AvatarGroup` and the `+N` chip read, so a new size is one row here.
 * - `size`: the box
 * - `font`: the letter inside an avatar
 * - `chipFont`: the `+N` text
 * - `overlap`: how far neighbouring avatars in a group overlap (25% of the box)
 */
export const avatarSizes = {
  xs: {size: '1rem', font: '8px', chipFont: '7px', overlap: '0.25rem'},
  compact: {size: '1.25rem', font: '9px', chipFont: '8px', overlap: '0.3125rem'},
  sm: {size: '1.5rem', font: '10px', chipFont: '10px', overlap: '0.375rem'},
  default: {size: '2rem', font: '0.75rem', chipFont: '0.75rem', overlap: '0.5rem'},
  lg: {size: '2.5rem', font: '0.875rem', chipFont: '0.875rem', overlap: '0.625rem'},
  xl: {size: '3.5rem', font: '1.25rem', chipFont: '1.25rem', overlap: '0.875rem'},
  '2xl': {size: '5rem', font: '1.5rem', chipFont: '1.5rem', overlap: '1.25rem'}
} as const;

/** Keys drive `AvatarSize`/`AvatarShape` and Storybook controls; the shape classes are `avatarShapeClasses` below, kept in sync by hand since Tailwind needs literal selectors. */
export const avatarVariantsConfig = {
  variants: {
    size: avatarSizes,
    shape: {
      circle: 'rounded-full',
      square: 'rounded-md'
    }
  },
  defaultVariants: {size: 'default', shape: 'circle'}
} as const;

export type AvatarSize = keyof typeof avatarSizes;
export type AvatarShape = keyof typeof avatarVariantsConfig.variants.shape;

export function avatarSizeStyle(size: AvatarSize): CSSProperties {
  const {size: box, font, chipFont, overlap} = avatarSizes[size];
  return {
    '--avatar-size': box,
    '--avatar-font': font,
    '--avatar-chip-font': chipFont,
    '--avatar-overlap': overlap
  } as CSSProperties;
}

/** Box dimensions, from the variables `avatarSizeStyle` sets — shared by `Avatar`/`AvatarSkeleton` so they can't drift. */
export const avatarSizeClasses = 'size-(--avatar-size)';

/** Corner radius keyed off `data-shape` — `xs` gets its own token since `rounded-md` exceeds half its 16px box. */
export const avatarShapeClasses =
  'data-[shape=square]:rounded-md data-[size=xs]:data-[shape=square]:rounded-(--avatar-xs-square-radius)';
